/**
 * Colour picker behaviour. The markup works without this script: every zone stays open and the radios are required.
 * With it, zones collapse to save space, the chosen colour name shows in each zone's summary, the next unanswered
 * zone opens after a choice, and a missing choice opens its zone and shows a message instead of failing silently.
 */
class ColourPicker extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.#handleChange);
    // `invalid` does not bubble, so listen in the capture phase.
    this.addEventListener('invalid', this.#handleInvalid, true);

    this.#zones.forEach((zone, index) => {
      const checked = zone.querySelector('input:checked');
      if (checked instanceof HTMLInputElement) this.#showChoice(zone, checked);

      const details = zone.querySelector('details');
      if (details && index > 0) details.open = false;
    });
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#handleChange);
    this.removeEventListener('invalid', this.#handleInvalid, true);
  }

  /** @returns {HTMLElement[]} */
  get #zones() {
    return Array.from(this.querySelectorAll('.colour-picker__zone'));
  }

  /**
   * @param {HTMLElement} zone
   * @param {HTMLInputElement} input
   */
  #showChoice(zone, input) {
    const valueElement = zone.querySelector('[data-value]');
    if (valueElement) valueElement.textContent = input.value;

    // Mirror the chosen swatch next to the zone name.
    const chip = zone.querySelector('[data-chip]');
    const swatch = input.nextElementSibling;
    if (chip instanceof HTMLElement && swatch instanceof HTMLElement) {
      chip.style.setProperty('--swatch-background', swatch.style.getPropertyValue('--swatch-background'));
      chip.classList.toggle('colour-picker__swatch--starry', swatch.classList.contains('colour-picker__swatch--starry'));
      chip.hidden = false;
    }
    zone.setAttribute('data-chosen', '');

    const error = zone.querySelector('[data-error]');
    if (error instanceof HTMLElement) {
      error.hidden = true;
      error.textContent = '';
    }
  }

  /** @param {Event} event */
  #handleChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;

    const zone = input.closest('.colour-picker__zone');
    if (!(zone instanceof HTMLElement)) return;

    this.#showChoice(zone, input);

    // Keyboard users move through radios with the arrow keys, which also fires change. Only advance on pointer use
    // so the list does not close under them.
    if (!input.matches(':focus-visible')) {
      const details = zone.querySelector('details');
      const next = this.#zones.find((candidate) => !candidate.querySelector('input:checked'));

      if (details) details.open = false;
      const nextDetails = next?.querySelector('details');
      if (nextDetails) nextDetails.open = true;
    }
  };

  /** @param {Event} event */
  #handleInvalid = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    const zone = input.closest('.colour-picker__zone');
    if (!(zone instanceof HTMLElement)) return;

    const details = zone.querySelector('details');
    if (details) details.open = true;

    const error = zone.querySelector('[data-error]');
    if (error instanceof HTMLElement && error.hidden) {
      const template = this.dataset.errorTemplate || '';
      error.textContent = template.replace('[zone]', zone.dataset.zone || '');
      error.hidden = false;
    }

    // Bring the first missing zone into view once per validation pass.
    const firstMissing = this.#zones.find((candidate) => !candidate.querySelector('input:checked'));
    if (firstMissing === zone) {
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      zone.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
    }
  };
}

if (!customElements.get('colour-picker')) {
  customElements.define('colour-picker', ColourPicker);
}
